import { useEffect, useState } from "react";
import { getCardRating, rateCard, type CardRating as Rating } from "@/lib/card-ratings.functions";
import { publishCardRating, useCardRating } from "@/lib/use-card-ratings";

/** Stable per-browser voter token, shared with the article reaction bar. */
function voterToken(): string {
  const key = "palstars-voter";
  let token = localStorage.getItem(key);
  if (!token || token.length < 8) {
    token = `v_${crypto.randomUUID()}`;
    localStorage.setItem(key, token);
  }
  return token;
}

function label(avg: number, votes: number) {
  if (!votes) return "Not rated yet";
  if (avg >= 4.5) return "Community favourite";
  if (avg >= 3.5) return "Well liked";
  if (avg >= 2.5) return "Mixed opinions";
  return "Unpopular pick";
}

/**
 * One-line community star rating for a card. No account needed: each browser
 * gets one vote per card (changing it replaces the same vote) and the server
 * additionally rate-limits per connection. The initial average/vote count is
 * server-rendered so the visible number always matches the page's
 * `aggregateRating` structured data.
 */
export function CardRatingBar({
  code,
  initial,
  className = "",
}: {
  code: string;
  initial?: { votes: number; average: number };
  className?: string;
}) {
  const [state, setState] = useState<Rating>({
    code,
    votes: initial?.votes ?? 0,
    average: initial?.average ?? 0,
    mine: 0,
  });
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCardRating({ data: { code, voter: voterToken() } });
        if (alive && data) setState(data);
      } catch {
        // Ratings are optional — never break the card page.
      }
    })();
    return () => {
      alive = false;
    };
  }, [code]);

  async function vote(value: number) {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      const data = await rateCard({ data: { code, voter: voterToken(), rating: value } });
      setState(data);
      setNote("Thanks — your rating is counted.");
    } catch (e) {
      setNote((e as Error).message || "Could not save your rating.");
    } finally {
      setBusy(false);
    }
  }

  const shown = hover || state.mine || Math.round(state.average);

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`}>
      <div
        className="flex items-center"
        role="group"
        aria-label={`Rate ${code} out of 5 stars`}
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={busy}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            aria-pressed={state.mine === n}
            onMouseEnter={() => setHover(n)}
            onFocus={() => setHover(n)}
            onClick={() => vote(n)}
            className="px-0.5 text-lg leading-none transition disabled:opacity-60"
          >
            <span className={n <= shown ? "text-amber-400" : "text-muted-foreground/40"} aria-hidden="true">
              ★
            </span>
          </button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        {state.votes ? (
          <>
            <span className="font-semibold text-foreground">{state.average.toFixed(1)}</span>/5 ·{" "}
            {state.votes} vote{state.votes === 1 ? "" : "s"} · {label(state.average, state.votes)}
          </>
        ) : (
          <>Be the first to rate {code} — no account needed.</>
        )}
      </p>
      {note && <span className="text-xs text-primary">{note}</span>}
    </div>
  );
}

/**
 * Compact inline stars for grids and product tiles (card database, card
 * marketplace, sealed/merch marketplace). Same server-side abuse controls as
 * the full bar: one vote per browser per item, IP-hash rate limited.
 */
export function RatingStars({
  code,
  className = "",
  label = "",
  size = "sm",
}: {
  code: string;
  className?: string;
  label?: string;
  /** "sm" fits dense grids; "lg" is the readable size used in popouts/lightboxes. */
  size?: "sm" | "lg";
}) {
  const starSize = size === "lg" ? "text-lg" : "text-[13px]";
  const textSize = size === "lg" ? "text-xs" : "text-[10px]";
  const hitPad = size === "lg" ? "px-0.5" : "";
  const [voter, setVoter] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // One batched request serves every rating widget on the page.
  const state = useCardRating(code, voter);

  useEffect(() => {
    setVoter(voterToken());
  }, []);

  async function vote(value: number) {
    if (busy) return;
    setBusy(true);
    try {
      publishCardRating(await rateCard({ data: { code, voter: voterToken(), rating: value } }));
    } catch {
      // Ignore — the visible average simply stays as it was.
    } finally {
      setBusy(false);
    }
  }

  const shown = state.mine || Math.round(state.average);
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="flex items-center" role="group" aria-label={`Rate ${label || code} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={busy}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            aria-pressed={state.mine === n}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void vote(n);
            }}
            className={`${hitPad} ${starSize} leading-none transition disabled:opacity-60`}
          >
            <span className={n <= shown ? "text-amber-400" : "text-muted-foreground/40"} aria-hidden="true">
              ★
            </span>
          </button>
        ))}
      </div>
      <span className={`${textSize} tabular-nums text-muted-foreground`}>
        {state.votes ? `${state.average.toFixed(1)} (${state.votes})` : "Rate"}
      </span>
    </div>
  );
}
